import React, { useState, FormEvent } from 'react';
import { PiCheckLight, PiEnvelopeSimpleLight, PiPhoneLight, PiMapPinLight } from 'react-icons/pi';
import { EnrollmentFormData } from '../types';
import { programmes, programmeOptions } from '../data/programmes';
import Cta from '../components/Cta';
import Reveal from '../components/Reveal';

const fieldClass = `w-full rounded-2xl border-0 bg-espresso-700/[0.04] px-5 py-4 text-[15px] text-espresso-900
  ring-1 ring-inset ring-espresso-700/[0.08] transition-all duration-700 ease-fluid
  placeholder:text-espresso-500/60 focus:bg-espresso-700/[0.02] focus:ring-2 focus:ring-sage/40`;

const EnrollPage: React.FC = () => {
  const [formData, setFormData] = useState<EnrollmentFormData>({
    name: '',
    email: '',
    phone: '',
    course: programmeOptions[0],
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      // Production serves the PHP endpoint alongside the built SPA; in dev it
      // runs separately via `php -S localhost:8000 -t server`.
      const apiUrl =
        process.env.NODE_ENV === 'production'
          ? '/server/enroll.php'
          : 'http://localhost:8000/enroll.php';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({
          text: 'Thank you for enrolling! We will contact you soon.',
          type: 'success',
        });
        setFormData({ name: '', email: '', phone: '', course: programmeOptions[0] });
      } else {
        setMessage({
          text: data.message || 'Something went wrong. Please try again.',
          type: 'error',
        });
      }
    } catch (error) {
      console.error('Enrollment error:', error);
      setMessage({ text: 'Server error. Please try again later.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prevState: EnrollmentFormData) => ({ ...prevState, [name]: value }));
  };

  return (
    <section
      id="enroll"
      className="min-h-[100dvh] bg-mesh-warm px-4 pb-32 pt-36 sm:px-8 sm:pt-44 lg:px-12"
    >
      <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-12 lg:gap-8">
        {/* Editorial Split: type + reassurance left, the form island right */}
        <div className="lg:col-span-5">
          <Reveal>
            <span className="eyebrow bg-plum-pale text-plum-deep">Enrolment</span>
            <h1 className="mt-7 font-display text-[3rem] font-light leading-[0.98] tracking-[-0.035em] text-espresso-900 sm:text-6xl">
              Enroll in Our Programmes
            </h1>
            <p className="mt-8 max-w-md text-[15px] leading-relaxed text-espresso-600">
              Fill in the form below to register for one of our three online training
              programmes. We respond personally to every enrolment — usually within two
              working days.
            </p>
          </Reveal>

          <Reveal delay={120}>
            <ul className="mt-12 space-y-3">
              {[
                'Delivered 100% online — study from anywhere',
                'Plus 5 additional days to complete and submit assignments',
                'Marked by qualified assessors, with a certificate on completion',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sage-pale text-sage-deep">
                    <PiCheckLight className="h-3 w-3" strokeWidth={1.5} />
                  </span>
                  <span className="text-sm text-espresso-600">{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={200}>
            <div className="mt-12 space-y-3 border-t border-espresso-700/10 pt-8">
              <a
                href="mailto:admin@leolilly.org"
                className="flex items-center gap-3 text-sm text-espresso-600 transition-colors duration-700 ease-fluid hover:text-espresso-900"
              >
                <PiEnvelopeSimpleLight className="h-4 w-4" strokeWidth={1} />
                admin@leolilly.org
              </a>
              <a
                href="tel:+27733544713"
                className="flex items-center gap-3 text-sm text-espresso-600 transition-colors duration-700 ease-fluid hover:text-espresso-900"
              >
                <PiPhoneLight className="h-4 w-4" strokeWidth={1} />
                +27 73 354 4713
              </a>
              <a
                href="tel:+27672594463"
                className="flex items-center gap-3 text-sm text-espresso-600 transition-colors duration-700 ease-fluid hover:text-espresso-900"
              >
                <PiPhoneLight className="h-4 w-4" strokeWidth={1} />
                +27 67 259 4463
              </a>
              <p className="flex items-start gap-3 text-sm text-espresso-600">
                <PiMapPinLight className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1} />
                39 Ronbex Road, Activia Park, Germiston, 1429
              </p>
            </div>
          </Reveal>
        </div>

        {/* Form island */}
        <Reveal delay={160} className="lg:col-span-7">
          <div className="bezel-shell-lg shadow-lifted">
            <div className="bezel-core-lg p-7 sm:p-10">
              {message.text && (
                <div
                  role="status"
                  aria-live="polite"
                  className={`message ${message.type} mb-8 rounded-2xl px-5 py-4 text-sm ring-1 ring-inset ${
                    message.type === 'success'
                      ? 'bg-sage-pale/60 text-sage-deep ring-sage/25'
                      : 'bg-red-50 text-red-700 ring-red-200'
                  }`}
                >
                  {message.text}
                </div>
              )}

              <form id="enrollment-form" onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="name"
                    className="mb-2 block text-[10px] uppercase tracking-eyebrow text-espresso-500"
                  >
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    required
                    className={fieldClass}
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="mb-2 block text-[10px] uppercase tracking-eyebrow text-espresso-500"
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    required
                    className={fieldClass}
                  />
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="mb-2 block text-[10px] uppercase tracking-eyebrow text-espresso-500"
                  >
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+27 82 000 0000"
                    required
                    className={fieldClass}
                  />
                </div>

                <div>
                  <label
                    htmlFor="course"
                    className="mb-2 block text-[10px] uppercase tracking-eyebrow text-espresso-500"
                  >
                    Select Programme
                  </label>
                  <select
                    id="course"
                    name="course"
                    value={formData.course}
                    onChange={handleChange}
                    className={`${fieldClass} appearance-none bg-[length:18px] bg-[right_1.25rem_center] bg-no-repeat pr-12`}
                    style={{
                      backgroundImage:
                        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%237A6A71' stroke-width='1.25'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
                    }}
                  >
                    {programmes.map((programme) => (
                      <option key={programme.id} value={programme.id}>
                        {programme.title} — {programme.duration}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="pt-2">
                  <Cta type="submit" disabled={loading}>
                    {loading ? 'Submitting…' : 'Submit'}
                  </Cta>
                </div>
              </form>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default EnrollPage;
